import React, { useState, useEffect } from 'react';
import { CountryNames, ISO3to2Mapping } from '../../shared/countries.js';
import { ROLE_LABELS, type AccountDto } from '../../shared/types/index.js';
import { useTranslation } from '../i18n/useTranslation.js';
import { getFlagEmoji } from '../utils/Helpers.js';

function nowTime() {
  return new Date().toLocaleTimeString('en-GB');
}

interface NavProps {
  totalAlerts: number;
  onOpenSettings: () => void;
  activeView: string;
  onChangeView: (id: string) => void;
  account: AccountDto | null;
  /** The admin behind an impersonation session. */
  impersonatedBy?: AccountDto | null;
  onStopImpersonation?: () => void;
  /** While impersonating: whether the admin acts with operator rights on this account's data. */
  editMode?: boolean;
  onEditModeChange?: (on: boolean) => void;
  /** Selected data-scope country (ISO3). */
  country?: string;
  onChangeCountry: (country: string) => void;
}

function countryLabel(iso3: string): string {
  const iso2 = ISO3to2Mapping[iso3];
  const name = (iso2 && CountryNames[iso2]) || iso3;
  return iso2 ? `${getFlagEmoji(iso2)} ${name}` : name;
}

function Nav({
  totalAlerts,
  onOpenSettings,
  activeView,
  onChangeView,
  account,
  impersonatedBy,
  onStopImpersonation,
  editMode = true,
  onEditModeChange,
  country,
  onChangeCountry,
}: NavProps) {
  const { t } = useTranslation();
  const [clock, setClock] = useState(nowTime);
  useEffect(() => {
    const timer = setInterval(() => setClock(nowTime()), 1000);
    return () => clearInterval(timer);
  }, []);

  const tabs = [
    { id: 'map', icon: '🗺', label: t('nav.tab.map') },
    { id: 'graph', icon: '🔗', label: t('nav.tab.graph') },
    { id: 'timeline', icon: '⏱', label: t('nav.tab.timeline') },
    { id: 'analysis', icon: '📊', label: t('nav.tab.analysis') },
  ];

  const initial = account?.displayName?.trim().charAt(0).toUpperCase() || '?';
  const accountTitle = account ? `${account.displayName} · ${ROLE_LABELS[account.role]}` : t('nav.signedOut');

  return (
    <div className="nav">
      <div className="nav-brand">
        <svg className="nav-logo" viewBox="0 0 40 40">
          <polygon points="20,3 37,12 37,28 20,37 3,28 3,12" fill="none" stroke="#d4af37" strokeWidth="1.5" />
          <polygon points="20,8 33,15 33,25 20,32 7,25 7,15" fill="rgba(212,175,55,.07)" />
          <polygon
            points="20,12 28,17 28,23 20,28 12,23 12,17"
            fill="none"
            stroke="rgba(212,175,55,.4)"
            strokeWidth="1"
          />
          <circle cx="20" cy="20" r="3" fill="#d4af37" />
        </svg>
        <div>
          <div className="ntm">{t('nav.brand')}</div>
          <div className="nts">{t('nav.subtitle')}</div>
        </div>
      </div>

      <div className="ndiv" />
      <div>
        <div className="nml">{t('nav.status')}</div>
        <div className="nmn" style={{ color: 'var(--grn)' }}>
          {t('nav.live')}
        </div>
      </div>
      <div className="ntabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`ntab ${activeView === tab.id ? 'on' : ''}`}
            onClick={() => onChangeView(tab.id)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      <div className="nr">
        {account && impersonatedBy && (
          <button
            type="button"
            className="nav-impersonating"
            onClick={onStopImpersonation}
            title={t('nav.returnToAccount', { name: impersonatedBy.displayName })}
          >
            <span className="nav-impersonating-label">{t('nav.impersonating', { name: account.username })}</span>
            <span className="nav-impersonating-return">{t('nav.returnToAccount', { name: impersonatedBy.displayName })}</span>
          </button>
        )}
        {account && impersonatedBy && onEditModeChange && (
          <label className={`nav-edit-mode ${editMode ? 'on' : ''}`} title={t('nav.editModeHint')}>
            <input
              type="checkbox"
              className="nav-switch"
              checked={editMode}
              onChange={(e) => onEditModeChange(e.target.checked)}
            />
            <span className="nav-switch-track">
              <span className="nav-switch-knob" />
            </span>
            <span>{t('nav.editMode')}</span>
          </label>
        )}
        {account && account.countries.length > 1 && (
          <select
            className="nav-country"
            value={country ?? account.countries[0]}
            onChange={(e) => onChangeCountry(e.target.value)}
            title={t('nav.country')}
            aria-label={t('nav.country')}
          >
            {account.countries.map((iso3) => (
              <option key={iso3} value={iso3}>
                {countryLabel(iso3)}
              </option>
            ))}
          </select>
        )}
        <div className="ldot" />
        <div className="abadge">
          <div className="abd" />
          <span className="abn">{totalAlerts}</span>
          <span className="abl">{t('nav.alerts')}</span>
        </div>
        <span className="nclk">{clock}</span>
        <div className="gear-btn" onClick={onOpenSettings} title="System Settings">
          ⚙
        </div>
        <div className="nusr" onClick={onOpenSettings} title={accountTitle}>
          {initial}
        </div>
      </div>
    </div>
  );
}

export default Nav;
